<template>
    <div class="card mb-2">
      <div class="card-header bg-primary text-white">
          <template  v-if="isMultiProject">
            New Multi-Property Service
          </template>
          <template v-else-if="isServiceEdit">
            <span class="font-weight-bold">
              Edit Service
            </span>
          </template>
          <template v-else>
            New Service
          </template>
      </div>
      <div class="card-body">
          <form @submit.prevent class="needs-validation" autocomplete="off" novalidate>
            <div v-if="serverError" class="alert alert-danger" role="alert">
              {{serverError}}
            </div>
            <LoadingSpinner v-if="submitting" message="Saving Service" size='1x' />
            <template v-if="!submitting">
              <div class="form-group row" v-if="isServiceEdit">
                <label class="col-lg-4 label-text">
                  Service ID
                </label>
                <div class="col-lg-8">
                  {{ currentService.id }}
                </div>
              </div>
              <div class="form-group row" v-if="isServiceEdit">
                <div class="col-lg-4 label-text">
                  Category
                </div>
                <div class="col-lg-8">
                  {{ getCategoryName(currentService.categoryId) }}
                </div>
              </div>
              <div class="form-group row" v-else>
                <label for="category" class="col-lg-4 label-text">
                  <span class="text-danger">*</span>Category
                </label>
                <div class="loading col-lg-5" v-if="categoriesLoading">
                  Loading...
                </div>
                <div class="col-lg-8">
                  <select v-if="!categoriesLoading"
                    v-validate="'required'"
                    data-vv-as="Category"
                    class="form-control"
                    name="category"
                    @change="onCategoryChange"
                    v-model="service.categoryId"
                  >
                    <option value="" disabled>Select Category</option>
                    <option v-for="option in getCategoriesForForm()"
                      :key="option.id"
                      :value="option.id"
                    >
                      {{ option.name }}
                    </option>
                  </select>
                  <div class="invalid-feedback">{{ errors.first('category') }}</div>
                </div>
              </div>
              <div class="form-group row">
                <label for="type" class="col-lg-4 col-form-label label-text">
                  <span class="text-danger">*</span>Component(s)
                </label>
                <div class="loading col-lg-5" v-if="typesLoading">
                    Loading...
                </div>
                <div class="col-lg-8" v-if="!service.categoryId">
                  <small class="form-text text-muted">
                    Select Category before choosing Components
                  </small>
                </div>
                <div class="col-lg-8">
                  <b-form-checkbox-group
                    v-if="!typesLoading"
                    v-validate="'multi_required'"
                    data-vv-as="Service Component"
                    v-model="service.typeIds"
                    name="serviceType"
                  >
                    <b-form-checkbox
                      v-for="option in filteredTypesByCategorySelected"
                      :key="option.id"
                      :value="option.id"
                      class="col-md-12 noselect">
                      {{ option.name }}
                    </b-form-checkbox>
                  </b-form-checkbox-group>
                  <div class="invalid-feedback d-block mb-2">{{ errors.first('serviceType') }}</div>
                </div>
              </div>
              <div class="form-group row" v-if="canEditStatus">
                <label for="status" class="col-lg-4 col-form-label label-text">
                  <span class="text-danger">*</span>Status
                </label>
                <div class="loading col-lg-5" v-if="statusesLoading">
                  Loading...
                </div>
                <div class="col-lg-8">
                  <select v-if="!statusesLoading"
                    v-validate="'required'"
                    data-vv-as="Status"
                    class="form-control"
                    name="status"
                    v-model="service.statusId"
                  >
                    <option v-for="option in statuses"
                      :key="option.id"
                      :value="option.id"
                    >
                      {{ option.name }}
                    </option>
                  </select>
                  <div class="invalid-feedback">{{ errors.first('status') }}</div>
                </div>
              </div>
              <div class="form-group row" v-else>
                <label for="status" class="col-lg-4 label-text">
                  <span class="text-danger">*</span>Status
                </label>
                <div class="col-lg-8">
                  <div>
                    {{ getStatusName(currentService.statusId) }}
                  </div>
                  <label class="small text-muted" v-if="openReviewsRemain()">
                    Services cannot be closed until all of their Open Reviews
                    have been completed or dismissed.
                  </label>
                </div>
              </div>
              <div class="form-group row" v-if="isMultiProject">
                <label for="properties" class="col-lg-4 label-text">
                <span class="text-danger">*</span>Associated Properties
                </label>
                <div class="col-lg-8">
                <div class="form-group row">
                  <div class="col-lg-11">
                    <input class="form-control"
                      :name="'propertyId-0'"
                      placeholder="Enter Property ID Number"
                      v-model="service.associatedPropertyIds[0]"
                      v-validate="{
                        required: true,
                        numeric: true,
                        current_property_exists: true,
                        unique: service.associatedPropertyIds
                      }"
                      data-vv-as="Property ID"
                      maxlength="10"
                      inputmode="numeric"
                      pattern="[0-9]*"
                    />
                    <div class="invalid-feedback d-block">{{ errors.first('propertyId-0') }}</div>
                  </div>
                  <div class="col-lg-1 pl-md-0 d-flex flex-row-reverse
                  flex-md-column justify-content-between justify-content-md-start
                  delete-vertical-offset" v-if="service.associatedPropertyIds.length > 2">
                    <div class="d-flex justify-content-end">
                      <b-button variant="danger" size="sm" class="mr-2 mb-2"
                        @click='removeAssociatedPropertyId(0)'>
                        <font-awesome-icon icon="trash-alt" class="text-white" fixed-width />
                      </b-button>
                    </div>
                  </div>
                </div>
                <div class="form-group row">
                  <div class="col-lg-11">
                    <input class="form-control"
                      :name="'propertyId-1'"
                      placeholder="Enter Property ID Number"
                      v-model="service.associatedPropertyIds[1]"
                      v-validate="{
                        required: true,
                        numeric: true,
                        current_property_exists: true,
                        unique: service.associatedPropertyIds
                      }"
                      data-vv-as="Property ID"
                      maxlength="10"
                      inputmode="numeric"
                      pattern="[0-9]*"
                    />
                    <div class="invalid-feedback d-block">{{ errors.first('propertyId-1') }}</div>
                  </div>
                  <div class="col-lg-1 pl-md-0 d-flex flex-row-reverse
                  flex-md-column justify-content-between justify-content-md-start
                  delete-vertical-offset" v-if="service.associatedPropertyIds.length > 2">
                    <div class="d-flex justify-content-end">
                      <b-button variant="danger" size="sm" class="mr-2 mb-2"
                        @click='removeAssociatedPropertyId(1)'>
                        <font-awesome-icon icon="trash-alt" class="text-white" fixed-width />
                      </b-button>
                    </div>
                  </div>
                </div>
                <div v-if="service.associatedPropertyIds.length > 2">
                  <div v-for="(propertyId, index) in service.associatedPropertyIds.slice(2)"
                    :key="index">
                    <div class="form-group row">
                      <div class="col-lg-11">
                        <input class="form-control"
                               :name="`propertyId-${index + 2}`"
                               placeholder="Enter Property ID Number"
                               v-model="service.associatedPropertyIds[index + 2]"
                               v-validate="{
                                 required: true,
                                 numeric: true,
                                 current_property_exists: true,
                                 unique: service.associatedPropertyIds
                               }"
                               data-vv-as="Property ID"
                               maxlength="10"
                               inputmode="numeric"
                               pattern="[0-9]*"
                               />
                        <div class="invalid-feedback">
                          {{ errors.first(`propertyId-${index + 2}`) }}
                        </div>
                      </div>
                      <div class="col-lg-1 pl-md-0 d-flex flex-row-reverse
                      flex-md-column justify-content-between justify-content-md-start
                      delete-vertical-offset" v-if="service.associatedPropertyIds.length > 2">
                        <div class="d-flex justify-content-end">
                          <b-button variant="danger" size="sm" class="mr-2 mb-2"
                            @click='removeAssociatedPropertyId(index + 2)'>
                            <font-awesome-icon icon="trash-alt" class="text-white" fixed-width />
                          </b-button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <a class="form-action-text text-secondary white-space-nowrap"
                     @click="addAdditionalProperty()">
                    + Add Additional Property
                  </a>
                </div>
                </div>
              </div>
              <div class="form-group row" v-else-if="!isServiceEdit">
                <label for="properties" class="col-lg-4 label-text">
                  <span class="text-danger">*</span>Associated Property
                </label>
                <div class="loading col-lg-4" v-if="propertiesLoading">
                  Loading...
                </div>
                <div class="col-lg-8">
                <b-form-radio-group
                  v-if="!propertiesLoading"
                  v-model="associatedPropertyId"
                  v-validate="'required'"
                  data-vv-as="Associated Property"
                  name="properties"
                >
                  <b-form-radio
                    v-for="property in orderedProjectProperties"
                    :key="property.id"
                    :value="property.id"
                    class="col-md-5 noselect mb-2">
                    {{ getPropertyLabel(property)}}
                  </b-form-radio>
                </b-form-radio-group>
                <div v-if="errors.first('properties')" class="invalid-feedback d-block mb-2">
                  {{ errors.first('properties') }}
                </div>
                </div>
              </div>
              <div class="form-group row" v-if="!needsClosedEvent || !isServiceEdit">
                <label for="agreementType" class="col-lg-4 label-text">
                  <span class="text-danger">*</span>Agreement Type
                </label>
                <div class="loading col-lg-5" v-if="agreementTypeOptionsLoading">
                  Loading...
                </div>
                <div class="col-lg-8">
                  <select v-if="!agreementTypeOptionsLoading"
                    v-validate="'required'"
                    data-vv-as="Agreement Type"
                    class="form-control"
                    name="agreementType"
                    v-model="service.agreementTypeId"
                  >
                    <option value="" disabled>Select Agreement Type</option>
                    <option v-for="option in agreementTypes"
                      :key="option.id"
                      :value="option.id"
                    >
                      {{ option.name }}
                    </option>
                  </select>
                  <div class="invalid-feedback">{{ errors.first('agreementType') }}</div>
                </div>
              </div>
              <div class="form-group row" v-else>
                <div class="col-lg-4 label-text">
                  Agreement Type
                </div>
                <div class="col-lg-8">
                   {{ getAgreementTypeName(service.agreementTypeId) }}
                </div>
              </div>
              <div class="form-group row" v-if="!isServiceEdit">
                <label class="col-lg-4 label-text">
                  <span class="text-danger">*</span>Requestor Name
                </label>
                <div class="col-lg-6">
                  {{ service.contact.crmContact | contactNameOrDefault }}
                </div>
                <div class="col-lg-2">
                  <SearchContactsModal
                    :modalId="'contactSearch'"
                    @set-contact="updateContact"
                    :parentType="'service'"
                  />
                </div>
              </div>
              <div class="form-group row" v-if="!isServiceEdit">
                <label class="col-lg-4 label-text">
                  <span class="text-danger">*</span>Requestor Address
                </label>
                <div class="col-lg-8"
                  v-html="$options.filters.contactAddress(service.contact.crmContact)">
                </div>
              </div>
              <div class="form-group row" v-if="!isServiceEdit">
                <div class="col-lg-4 label-text"></div>
                <div class="col-lg-6">
                  <div v-if="contactError" class="invalid-feedback d-block">Requestor Contact is required.</div>
                </div>
              </div>
              <div class="form-group row" v-if="!isServiceEdit">
                <label for="roles" class="col-lg-4 label-text">
                  <span class="text-danger">*</span>Roles
                </label>
                <div class="loading col-lg-5" v-if="rolesLoading">
                  Loading...
                </div>
                <div class="col-lg-8">
                  <b-form-checkbox-group
                    v-if="!rolesLoading"
                    v-model="service.contact.roleIds"
                    v-validate="'requestor_role_required|at_least_two_required'"
                    data-vv-as="role"
                    name="roles"
                  >
                    <b-form-checkbox
                      v-for="role in contactRoles"
                      :key="role.id"
                      :value="role.id"
                      class="col-md-5 noselect">
                      {{ role.name }}
                    </b-form-checkbox>
                  </b-form-checkbox-group>
                  <div v-if="errors.first('roles')" class="invalid-feedback d-block mb-2">
                    {{ errors.first('roles') }}
                  </div>
                </div>
              </div>

              <div class="form-group row">
                <label for="serviceNotes" class="col-lg-4 label-text">
                  Service Notes
                </label>
                <div class="col-lg-8">
                  <textarea data-vv-as="Service Notes"
                    type="textarea"
                    class="form-control"
                    name="serviceNotes"
                    v-model="service.notes"
                    placeholder="Enter Notes"
                    rows="3"
                    maxlength="1000"
                  />
                  <small v-if="!errors.first('notes')" class="form-text text-muted">
                    Max 1000 characters
                  </small>
                  <div class="invalid-feedback">{{ errors.first('notes') }}</div>
                </div>
              </div>
              <div v-if="!isServiceEdit">
                <hr>
                <div class="label-text title-buffer">Open Event Required</div>
                <EventRequiredForm :editEvent="openEvent" />
                <template v-if="serviceCategoryIsInspection">
                  <hr>
                  <div class="label-text title-buffer">Inspection Details</div>
                  <InspectionServiceForm
                    :inspectionService="service"
                    :serviceTypeIsImagery="isInspectionServiceImagery(service)"/>
                </template>
              </div>

              <!-- if closed status AND is an inspection service -->
              <template v-if="needsClosedEvent">
                  <template v-if="serviceCategoryIsInspection && !isExistingInspectionEvent">
                    <hr>
                    <div class="label-text title-buffer">Inspection Event Required</div>
                    <EventRequiredForm :editEvent="inspectionEvent" />
                  </template>
                <hr>
                <div class="label-text title-buffer">Closed Event Required</div>
                <EventRequiredForm :editEvent="closedEvent" />
              </template>
              <StaffLeadAssignment
                v-if="!servicesLoading"
                :staffLead="service.staffLead"
                :staffLeadName="service.staffLeadName"
                :showSystemDefaultWarning="isServiceEdit"
                @update-staff-lead-selected="updateStaffLeadSelected"
              ></StaffLeadAssignment>
              <div class="form-group row">
                <label for="product" class="col-lg-4 label-text">
                  Service Product(s)
                </label>
                <div class="loading col-lg-5" v-if="productsLoading">
                  Loading...
                </div>
                <div class="col-lg-8">
                  <b-form-checkbox-group
                    v-if="!productsLoading"
                    v-model="service.productIds"
                    name="products"
                  >
                    <b-form-checkbox
                      v-for="product in products"
                      :key="product.id"
                      :value="product.id"
                      class="col-md-12 noselect">
                      {{ product.name }}
                    </b-form-checkbox>
                  </b-form-checkbox-group>
                </div>
              </div>
              <div class="form-group row">
                <label for="feeCharged" class="col-lg-4 col-form-label label-text">
                  Fee Charged
                </label>
                <div class="col-lg-8">
                  <money data-vv-as="Fee Charged"
                    v-model="service.feeCharged"
                    v-bind="moneyFormat"
                    type="text"
                    class="form-control"
                    :class="{'is-invalid': errors.first('feeCharged')}"
                    name="feeCharged"
                    placeholder="Enter Fee Charged"
                    maxlength="30"
                    v-validate="'min_value:0'"
                  />
                  <div class="invalid-feedback" style="display:block">
                    {{ errors.first('feeCharged') }}
                  </div>
                </div>
              </div>
              <div class="form-group row">
                <label for="feePaid" class="col-lg-4 label-text">
                  Fee Paid
                </label>
                <div class="col-lg-8">
                  <b-form-checkbox
                    v-model="service.feePaid"
                    name="feePaid"
                  >
                  </b-form-checkbox>
                </div>
              </div>
              <div class="form-group row">
                <label for="previousService" class="col-lg-4 col-form-label label-text">
                  Previous Service
                </label>
                <div class="loading col-lg-5" v-if="servicesLoading">
                  Loading...
                </div>
                <div class="col-lg-8" v-if="!servicesLoading">
                  <div v-if="!possiblePreviousServices.length">No Previous Services</div>
                  <select v-if="possiblePreviousServices.length"
                    class="form-control"
                    name="previousService"
                    v-model="service.previousServiceId"
                  >
                    <option :value="null">Select Previous Service</option>
                    <option v-for="option in possiblePreviousServices"
                      :key="option.id"
                      :value="option.id"
                    >
                      {{ `${option.id}` }}
                    </option>
                  </select>
                </div>
              </div>
            </template>
          </form>
      </div>
      <div class='card-footer right-align-content'>
        <b-button variant="danger"
          class="button-row-spacing"
          v-b-modal="'cancelServiceForm'"
          :disabled="submitting">
          Cancel
        </b-button>
        <b-button @click="submitWrapper()"
          :disabled="submitting">
          Save
        </b-button>
      </div>
      <CancelModal
        :modalId="'cancelServiceForm'"
        @cancelled="cancelConfirmed">
      </CancelModal>
    </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex';
import { Money } from 'v-money';
import api from '@/api';
import CancelModal from '@/components/CancelModal.vue';
import EventRequiredForm from '@/components/event/EventRequiredForm.vue';
import FormSubmit from '@/mixins/FormSubmit';
import InspectionServiceForm from '@/components/service/InspectionServiceForm.vue';
import { LookupModules, EntityModules } from '@/store/moduleConfig';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import StaffLeadAssignment from '@/components/common/StaffLeadAssignment.vue';
import SearchContactsModal from '@/components/contacts/SearchContactsModal.vue';
import EventDateHelper from '../../mixins/EventDateHelper';

export default {
  name: 'ServiceForm',
  components: {
    CancelModal,
    EventRequiredForm,
    InspectionServiceForm,
    LoadingSpinner,
    Money,
    StaffLeadAssignment,
    SearchContactsModal,
  },
  props: ['isMultiProject', 'isNewService'],
  directives: { money: Money },
  mixins: [FormSubmit, EventDateHelper],
  data() {
    return {
      newPropertyId: null,
      propertyEntryKey: 0,
      contactError: true,
      // this is just a holder of the value from the radio button
      associatedPropertyId: null,
      vofCrmContact: null,
      service: {
        categoryId: '',
        typeIds: [],
        statusId: 1,
        previousServiceId: null,
        agreementTypeId: '',
        associatedPropertyIds: [],
        events: [],
        observations: [],
        imageryAssessmentBeforeDate: null,
        beforeSource: '',
        imageryAssessmentAfterDate: null,
        afterSource: '',
        landownerPropertyPlans: '',
        questionsForVof: '',
        isServiceEdit: false,
        contactError: false,
        staffLead: null,
        staffLeadName: null,
        contact: {
          // defaulting requestor and grantee
          roleIds: [39, 24],
          serviceId: null,
          crmContact: null,
          crmId: null,
        },
      },
      agreementTypes: [],
      agreementTypeOptionsLoading: false,
      openEvent: {
        id: null,
        eventTypeId: 29,
        eventTypeName: 'Open',
        notes: null,
        date: null,
      },
      closedEvent: {
        id: null,
        eventTypeId: 30,
        eventTypeName: 'Closed',
        notes: null,
        date: null,
      },
      inspectionEvent: {
        id: null,
        eventTypeId: 31,
        eventTypeName: 'Inspection',
        notes: null,
        date: null,
      },
      propertiesLoading: true,
      moneyFormat: {
        decimal: '.',
        thousands: ',',
        prefix: '$ ',
        suffix: '',
        precision: 2,
        masked: false,
      },
    };
  },
  watch: {
    // eslint-disable-next-line func-names
    'service.associatedPropertyIds': function () {
      if (this.arePropertIdsValid(this.service.associatedPropertyIds)) {
        this.getAgreementTypesByPropertyIds(this.service.associatedPropertyIds);
      }
    },
    associatedPropertyId() {
      this.getAgreementTypesByPropertyIds([this.associatedPropertyId]);
    },
  },
  created() {
    this.getProperties();
    this.$store.dispatch('Service/fetchServices', parseInt(this.$route.params.project_id, 10));
    this.$store.dispatch(`${LookupModules.SERVICE_CATEGORIES.namespace}/fetchOptions`,
      LookupModules.SERVICE_CATEGORIES.url);
    this.$store.dispatch(`${LookupModules.SERVICE_TYPES.namespace}/fetchOptions`,
      LookupModules.SERVICE_TYPES.url);
    this.$store.dispatch(`${LookupModules.SERVICE_STATUSES.namespace}/fetchOptions`,
      LookupModules.SERVICE_STATUSES.url);
    this.$store.dispatch(`${LookupModules.SERVICE_PRODUCTS.namespace}/fetchOptions`,
      LookupModules.SERVICE_PRODUCTS.url);
    this.$store.dispatch(`${LookupModules.SERVICE_ROLES.namespace}/fetchOptions`,
      LookupModules.SERVICE_ROLES.url);
  },
  computed: {
    ...mapState('Service', {
      submitting: state => state.submitting,
      servicesLoading: state => state.loading,
      projectId: state => state.projectId,
      currentService: state => state.currentService,
      isServiceEdit: state => state.isServiceEdit,
      serverError: state => state.serverError,
    }),
    ...mapState(LookupModules.SERVICE_CATEGORIES.namespace, {
      categoriesLoading: state => state.loading,
    }),
    ...mapState(LookupModules.AGREEMENT_TYPES.namespace, {
      agreementTypesLoading: state => state.loading,
    }),
    ...mapState(LookupModules.SERVICE_PRODUCTS.namespace, {
      productsLoading: state => state.loading,
    }),
    ...mapState(EntityModules.SERVICE_REVIEW.namespace, {
      serviceReviews: state => state.entities,
    }),
    ...mapState(LookupModules.SERVICE_ROLES.namespace, {
      rolesLoading: state => state.loading,
    }),
    ...mapState(LookupModules.SERVICE_STATUSES.namespace, {
      statusesLoading: state => state.loading,
    }),
    ...mapState(LookupModules.SERVICE_TYPES.namespace, {
      typesLoading: state => state.loading,
    }),
    ...mapGetters('Service', ['orderedServices']),
    ...mapGetters(LookupModules.SERVICE_CATEGORIES.namespace, {
      getCategoryName: 'getName',
      categories: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.SERVICE_ROLES.namespace, {
      contactRoles: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.AGREEMENT_TYPES.namespace, {
      getAgreementTypeName: 'getName',
    }),
    ...mapGetters(LookupModules.SERVICE_TYPES.namespace, {
      getTypeName: 'getName',
      types: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.SERVICE_STATUSES.namespace, {
      getStatusName: 'getName',
      statuses: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.SERVICE_PRODUCTS.namespace, {
      products: 'getActiveOptions',
    }),
    ...mapGetters('Service', ['isInspectionServiceImagery']),
    needsClosedEvent() {
      // only require closed event IF it is a new service and is closed
      // OR if the current service is being changed to closed
      return (this.isNewService && this.service.statusId === 2)
      || (this.currentService.statusId !== 2 && this.service.statusId === 2);
    },
    isExistingInspectionEvent() {
      return this.service.events.some(x => x.id !== null && x.eventTypeId === 31);
    },
    orderedProjectProperties() {
      return this.projectProperties.slice(0).sort(
        (a, b) => b.calculatedAcres - a.calculatedAcres || b.id - a.id,
      );
    },
    possiblePreviousServices() {
      return this.orderedServices.filter(svc => svc.id !== this.currentService.id);
    },
    filteredTypesByCategorySelected() {
      return this.types.filter(t => t.serviceCategoryId === this.service.categoryId);
    },
    serviceCategoryIsInspection() {
      return this.service.categoryId === 1;
    },
    serviceCategoryIsReview() {
      return this.service.categoryId === 5;
    },
    serviceCategoryIsEducation() {
      return this.service.categoryId === 6;
    },
    serviceCategoryIsFacilities() {
      return this.service.categoryId === 7;
    },
    canEditStatus() {
      return (this.isServiceEdit)
        || this.isNewService
        || (this.currentService.statusId === 2 && this.isUserAdmin())
        || (this.isMultiProject && !this.currentService.Id);
    },
  },
  mounted() {
    if (this.isServiceEdit) {
      Object.assign(this.service, this.currentService);
      if (this.currentService.associatedPropertyIds.length > 0) {
        // eslint-disable-next-line
        this.associatedPropertyId = this.currentService.associatedPropertyIds[0];
      }
      this.getAgreementTypesByPropertyIds(this.service.associatedPropertyIds);
    }
    if (this.isMultiProject) {
      this.service.associatedPropertyIds = ['', ''];
    }
    if (!this.isServiceEdit) {
      this.getVofCrmContact();
    }
  },
  methods: {
    arePropertIdsValid(propertyIds) {
      let areAllIntegers = true;
      if (propertyIds === null || propertyIds === undefined || propertyIds.length === 0) return false;
      // if (this.errors.items.length > 0 && this.errors.items.find(x => x.field.includes('propertyId'))) return false;

      propertyIds.forEach((x) => {
        // eslint-disable-next-line no-restricted-globals
        if (x === null || x.length === 0 || isNaN(x)) {
          areAllIntegers = false;
        }
      });

      return areAllIntegers;
    },
    onCategoryChange() {
      // reset service types so values don't persist
      this.service.typeIds = [];

      // update contact if there is not already a value
      if ((this.serviceCategoryIsInspection || this.serviceCategoryIsEducation || this.serviceCategoryIsFacilities)
        && this.service.contact.crmContact === null) {
        this.service.contact.roleIds = [39, 24];
        this.service.contact.crmContact = this.vofCrmContact;
        this.service.contact.crmId = this.vofCrmContact.crmId;
      }
      if (this.serviceCategoryIsReview && (this.service.contact.crmContact === this.vofCrmContact || this.service.contact.crmContact === null)) {
        this.service.contact.roleIds = [39];
        this.service.contact.crmContact = null;
        this.service.contact.crmId = null;
      }
      this.checkContactError();
    },
    updateContact(contactResult) {
      this.service.contact.crmContact = contactResult;
      this.service.contact.crmId = contactResult.crmId;
      this.checkContactError();
    },
    getCategoriesForForm() {
      if (this.isMultiProject) {
        return this.categories.filter(cat => cat.isMultiProjectEligible);
      }
      return this.categories;
    },
    getVofCrmContact() {
      api.get('/contact/getVofCrmContact')
        .then(({ data }) => {
          this.vofCrmContact = data;
        }).catch((error) => {
          this.serverError = error.response.data;
        });
    },
    getProperties() {
      this.propertiesLoading = true;
      api.get(`/project/${this.$route.params.project_id}/currentproperties`)
        .then((response) => {
          this.projectProperties = response.data;
          this.propertiesLoading = false;
        });
    },
    getAgreementTypesByPropertyIds(propertyIds) {
      this.agreementTypeOptionsLoading = true;
      const ids = propertyIds.map(x => parseInt(x, 10));
      api.post('/property/getagreementtypesbypropertyids', ids)
        .then((response) => {
          this.agreementTypes = response.data;

          // reset the agreement type if not on edit.
          // ensures valid types can only be selected on create
          if (!this.isServiceEdit) {
            this.service.agreementTypeId = '';
          }

          if (response.data.length === 1) {
            this.service.agreementTypeId = response.data[0].id;
          }
          this.agreementTypeOptionsLoading = false;
        });
    },
    openReviewsRemain() {
      return this.serviceReviews && this.serviceReviews.some(review => review.reviewStatusId === 1);
    },
    getPropertyLabel(property) {
      if (property.calculatedAcres) {
        return `${property.id} - ${property.calculatedAcres} Acres`;
      }
      return `${property.id} - Unspecified Acreage`;
    },
    updateStaffLeadSelected(newStaffLead) {
      this.service.staffLeadSelected = newStaffLead;
    },
    getObservationText(textboxId) {
      return document.getElementById(textboxId).value;
    },
    getObservationAckTypeId(observationTypeId) {
      return document.getElementById(observationTypeId).value;
    },
    submitWrapper() {
      if (this.service.staffLeadSelected) {
        this.service.staffLead = this.service.staffLeadSelected.id;
        this.service.staffLeadName = this.service.staffLeadSelected.name;
      }
      if (!this.isServiceEdit) {
        this.service.events.splice(0, this.service.events.length);
        this.service.events.push(this.openEvent);
      }
      // push the value from the radio button into the array for submit
      if (this.associatedPropertyId) {
        this.service.associatedPropertyIds.splice(0);
        this.service.associatedPropertyIds.push(this.associatedPropertyId);
      }

      // logic for events

      // if events exist, then filter out any that
      if (this.service.events && this.isServiceEdit) {
        this.service.events = this.service.events.filter(x => x.id !== null);
      }
      // always add closed event if status is closed
      if (this.needsClosedEvent) {
        if (!this.service.events) {
          this.service.events = [];
          this.service.events.push(this.closedEvent);
        } else {
          this.service.events.push(this.closedEvent);
        }
      }
      // logic for inspection events
      // if service is an inspection and it is being closed, then!
      if (this.serviceCategoryIsInspection && this.needsClosedEvent) {
        // always add inspection even IF new inspection service
        if (!this.isServiceEdit) {
          this.service.events.push(this.inspectionEvent);
        }
        // Inspection Event required On Edit and if there are no other inspection events
        if (this.isServiceEdit && !this.isExistingInspectionEvent) {
          this.service.events.push(this.inspectionEvent);
        }
      }

      // let observationError = false;
      if (this.serviceCategoryIsInspection) {
        if (!this.isInspectionServiceImagery(this.service)) {
          this.service.imageryAssessmentBeforeDate = null;
          this.service.imageryAssessmentAfterDate = null;
          this.service.afterSource = '';
          this.service.beforeSource = '';
        }
        this.service.observations = this.$store.state.Service.currentService.observations;
        // observationError = this.service.observations === null || this.service.observations.length === 0;
      }
      if (this.isServiceEdit) {
        this.service.isServiceEdit = true;
        this.onSubmit(this.service, 'Service', null, this.routeToExistingService);
      } else {
        this.onSubmit(this.service, 'Service', [this.contactError], this.routeToNewService);
      }
    },
    checkContactError() {
      this.contactError = !this.service.contact.crmId;
    },
    cleanUpState() {
      this.$store.dispatch('Common/stopEditMode', 'Service');
      this.$store.dispatch(`${EntityModules.SERVICE_EVENT.namespace}/fetchEntities`, {
        parentType: EntityModules.SERVICE_EVENT.parentType,
        parentId: this.currentService.id,
        entityType: EntityModules.SERVICE_EVENT.entityType,
      });
    },
    routeToExistingService() {
      this.cleanUpState();
      this.$router.push({
        name: 'projectService',
        params: {
          project_id: parseInt(this.$route.params.project_id, 10),
          id: parseInt(this.$route.params.id, 10),
        },
      });
    },
    routeToNewService(id) {
      this.cleanUpState();
      this.$store.dispatch('Service/forceFetchServices', parseInt(this.$route.params.project_id, 10));
      this.$router.push({
        name: 'projectService',
        params: {
          project_id: this.isMultiProject ? this.projectId
            : parseInt(parseInt(this.$route.params.project_id, 10), 10),
          id,
        },
      });
    },
    cancelConfirmed() {
      this.$store.dispatch('Service/stopEditing');
      this.$store.dispatch('Common/stopEditMode', 'Service');
      if (this.isMultiProject) {
        this.$router.push({
          name: 'home',
        });
      } else if (this.isNewService) {
        this.$router.push({
          name: 'projectServices',
          params: { project_id: parseInt(this.$route.params.project_id, 10) },
        });
      } else {
        this.$router.push({
          name: 'projectService',
          params: {
            project_id: parseInt(this.$route.params.project_id, 10),
            id: this.currentService.id,
          },
        });
      }
    },
    validateClosed() {
      this.$validator.validate('closedDate');
    },
    addProperty() {
      this.service.associatedPropertyIds.push(this.newPropertyId);
      this.newPropertyId = null;
      this.propertyEntryKey += 1;
    },
    addAdditionalProperty() {
      this.service.associatedPropertyIds.push('');
    },
    removeAssociatedPropertyId(index) {
      this.service.associatedPropertyIds.splice(index, 1);
    },
    isUserAdmin() {
      const roles = this.$store.getters['User/getCurrentUser'].idToken ? this.$store.getters['User/getCurrentUser'].idToken.roles.join(', ') : '';
      return roles.includes('Admin');
    },
  },
};
</script>
